"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";
import svgMobileAbout from "@/assets/svg/svg-vyl6rp2s4v";
import imgHeroImageRight from "@/assets/images/landing/b7cf068f2cd000acb93c2d6dcf2c5d20fcc2d70c.png";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";

export default function MobileAboutUsContent() {
	const [activeCard, setActiveCard] = useState<number | null>(null);
	const [isUpgrading, setIsUpgrading] = useState(false);

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	const baseCardClasses =
		"backdrop-blur-md bg-[rgba(255,255,255,0.05)] border rounded-[17px] p-6 flex flex-col gap-5 transition-all duration-300 transform";

	const getCardClasses = (index: number) => {
		const isActive = activeCard === index;
		const borderClass = isActive
			? "border-[#A7FF5E]/60 shadow-[0_0_0_1px_rgba(167,255,94,0.4)] scale-[1.02]"
			: "border-[rgba(255,255,255,0.1)] scale-100";
		return `${baseCardClasses} ${borderClass}`;
	};

	const handleCardClick = (index: number) => {
		setActiveCard((current) => (current === index ? null : index));
	};

	return (
		<div
			className="bg-[#16251c] min-h-screen w-full overflow-x-hidden"
			style={{ width: "100%" }}
		>
			{/* Container with max width for mobile */}
			<div
				className="mx-auto"
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "2rem",
					alignItems: "center",
				}}
			>
				{/* Hero Section with Background */}
				<div className="relative h-[520px] bg-[#1a2c20] overflow-hidden mb-8">
					{/* Background building images */}
					<Image
						src={imgHeroImageRight}
						alt=""
						width={365}
						height={227}
						style={{ top: "340px" }}
						className="absolute left-[128px] top-[210px] w-[365px] h-[227px] object-cover max-w-none pointer-events-none"
					/>

					{/* Hero Content */}
					<div
						className="relative z-10 px-6 pt-8"
						style={{ marginTop: "8rem" }}
					>
						<h1 className="text-white text-[32px] leading-[38.4px] mb-6 tracking-tight">
							About Us
						</h1>
						<p className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed">
							We exist to give serious investors a faster,
							smarter, and more transparent way to research tax
							liens and deeds. With verified legal, financial, and
							property intelligence at your fingertips, Tax Lien
							Vest empowers you to make confident investment
							decisions in minutes, not days.
						</p>
					</div>
				</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "2rem",
						width: "90%",
						alignItems: "center",
					}}
				>
					<div className="flex flex-col">
						{/* Our Mission */}
						<div className="mx-4 mb-6">
							<button
								type="button"
								onClick={() => handleCardClick(0)}
								className={getCardClasses(0)}
							>
								<div
									className="backdrop-blur-[9.333px] rounded-[66.111px] w-14 h-14 flex items-center justify-center overflow-hidden"
									style={{
										backgroundImage:
											"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)",
									}}
								>
									<div className="w-[31.111px] h-[31.111px]">
										<svg
											className="block size-full"
											fill="none"
											viewBox="0 0 32 32"
										>
											<mask
												height="32"
												id="mask0_mission"
												maskUnits="userSpaceOnUse"
												style={{ maskType: "alpha" }}
												width="32"
												x="0"
												y="0"
											>
												<rect
													fill="#CBF7A6"
													height="31.1111"
													width="31.1111"
												/>
											</mask>
											<g mask="url(#mask0_mission)">
												<path
													d={svgMobileAbout.p1e0bdd00}
													fill="#25BC45"
												/>
											</g>
										</svg>
									</div>
								</div>
								<div className="flex flex-col gap-1 tracking-[0.5px]">
									<h3
										className="text-white text-base font-bold leading-6"
										style={{ textAlign: "left" }}
									>
										Our Mission
									</h3>
									<p
										className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed"
										style={{ textAlign: "left" }}
									>
										We exist to give serious investors a
										faster, smarter, and more transparent
										way to research tax liens and deeds.
										With verified legal, financial, and
										property intelligence at your
										fingertips, Tax Lien Vest empowers you
										to make confident investment decisions
										in minutes, not days.
									</p>
								</div>
							</button>
						</div>

						{/* Our Vision */}
						<div className="mx-4 mb-6">
							<button
								type="button"
								onClick={() => handleCardClick(1)}
								className={getCardClasses(1)}
							>
								<div
									className="backdrop-blur-[9.333px] rounded-[66.111px] w-14 h-14 flex items-center justify-center overflow-hidden"
									style={{
										backgroundImage:
											"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)",
									}}
								>
									<div className="w-[31.111px] h-[31.111px]">
										<svg
											className="block size-full"
											fill="none"
											viewBox="0 0 32 32"
										>
											<mask
												height="32"
												id="mask0_vision"
												maskUnits="userSpaceOnUse"
												style={{ maskType: "alpha" }}
												width="32"
												x="0"
												y="0"
											>
												<rect
													fill="#CBF7A6"
													height="31.1111"
													width="31.1111"
												/>
											</mask>
											<g mask="url(#mask0_vision)">
												<path
													d={svgMobileAbout.p1e0bdd00}
													fill="#25BC45"
												/>
											</g>
										</svg>
									</div>
								</div>
								<div className="flex flex-col gap-1 tracking-[0.5px]">
									<h3
										className="text-white text-base font-bold leading-6"
										style={{ textAlign: "left" }}
									>
										Our Vision
									</h3>
									<p
										className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed"
										style={{ textAlign: "left" }}
									>
										We exist to give serious investors a
										faster, smarter, and more transparent
										way to research tax liens and deeds.
										With verified legal, financial, and
										property intelligence at your
										fingertips, Tax Lien Vest empowers you
										to make confident investment decisions
										in minutes, not days.
									</p>
								</div>
							</button>
						</div>
					</div>

					{/* Our Story */}
					<div className="mx-4 mb-6">
						<div className="flex flex-col gap-2 tracking-[0.5px]">
							<h2
								className="text-white text-xl font-bold leading-7"
								style={{ textAlign: "left" }}
							>
								Our Story
							</h2>
							<p
								className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed"
								style={{ textAlign: "left" }}
							>
								Tax Lien Vest was founded to solve a common
								investor challenge: tax lien investing is
								lucrative but time-consuming and confusing. Our
								founders built a platform that delivers
								institutional-grade property intelligence—once
								available only to insiders—in an easy-to-use,
								investor-focused system.
							</p>
						</div>
					</div>

					{/* What We Offer */}
					<div className="mx-4 mb-6">
						<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
							What We Offer
						</h2>

						{/* Smarter Property Research */}
						<button
							type="button"
							onClick={() => handleCardClick(2)}
							className={`${getCardClasses(2)} mb-6`}
						>
							<div className="w-[57.006px] h-14">
								<svg
									className="block size-full"
									fill="none"
									viewBox="0 0 57 56"
								>
									<path
										d={svgMobileAbout.p12567ab0}
										fill="#25BC45"
									/>
								</svg>
							</div>
							<div className="flex flex-col gap-1 tracking-[0.5px]">
								<h3
									className="text-white text-base font-bold leading-6"
									style={{ textAlign: "left" }}
								>
									Smarter Property Research
								</h3>
								<p
									className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed"
									style={{ textAlign: "left" }}
								>
									Access geographic, financial, legal, and
									owner data to screen, analyse, and act on
									opportunities with confidence.
								</p>
							</div>
						</button>

						{/* Auction-Ready Intelligence */}
						<button
							type="button"
							onClick={() => handleCardClick(3)}
							className={`${getCardClasses(3)} mb-6`}
						>
							<div className="w-14 h-[59.039px]">
								<svg
									className="block size-full"
									fill="none"
									viewBox="0 0 56 60"
								>
									<path
										d={svgMobileAbout.p36904c00}
										fill="#25BC45"
									/>
								</svg>
							</div>
							<div className="flex flex-col gap-1 tracking-[0.5px]">
								<h3
									className="text-white text-base font-bold leading-6"
									style={{ textAlign: "left" }}
								>
									Auction-Ready Intelligence
								</h3>
								<p
									className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed"
									style={{ textAlign: "left" }}
								>
									From sheriff sale dates to total debt
									amounts, we provide the data you need to
									prepare winning bids.
								</p>
							</div>
						</button>

						{/* Opportunity Zone Targeting */}
						<button
							type="button"
							onClick={() => handleCardClick(4)}
							className={getCardClasses(4)}
						>
							<div className="w-14 h-14">
								<svg
									className="block size-full"
									fill="none"
									viewBox="0 0 56 56"
								>
									<path
										d={svgMobileAbout.pd9b3f00}
										stroke="#23AF41"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="6.22222"
									/>
								</svg>
							</div>
							<div className="flex flex-col gap-1 tracking-[0.5px]">
								<h3
									className="text-white text-base font-bold leading-6"
									style={{ textAlign: "left" }}
								>
									Opportunity Zone Targeting
								</h3>
								<p
									className="text-[rgba(255,255,255,0.7)] text-sm leading-relaxed"
									style={{ textAlign: "left" }}
								>
									Find tax-advantaged properties using our
									integrated Qualified Opportunity Zone
									filters.
								</p>
							</div>
						</button>
					</div>

					{/* Our Values */}
					<div className="mx-4 mb-8">
						<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
							Our Values
						</h2>

						<div className="flex flex-col gap-5">
							{/* Transparency */}
							<div className="flex gap-3 items-start">
								<div className="w-10 h-10 shrink-0">
									<svg
										className="block size-full"
										fill="none"
										viewBox="0 0 40 40"
									>
										<mask
											height="40"
											id="mask0_transparency"
											maskUnits="userSpaceOnUse"
											style={{ maskType: "alpha" }}
											width="40"
											x="0"
											y="0"
										>
											<rect
												fill="#D9D9D9"
												height="40"
												width="40"
											/>
										</mask>
										<g mask="url(#mask0_transparency)">
											<path
												d={svgMobileAbout.p1970e580}
												fill="#25BC45"
											/>
										</g>
									</svg>
								</div>
								<div className="flex-1 leading-5 tracking-[0.5px]">
									<h3 className="text-white text-sm font-black mb-0">
										Transparency
									</h3>
									<p className="text-[rgba(255,255,255,0.7)] text-xs">
										All our data is sourced from official
										legal and financial records.
									</p>
								</div>
							</div>

							{/* Integrity */}
							<div className="flex gap-3 items-start">
								<div className="w-10 h-10 shrink-0">
									<svg
										className="block size-full"
										fill="none"
										viewBox="0 0 40 40"
									>
										<mask
											height="40"
											id="mask0_integrity"
											maskUnits="userSpaceOnUse"
											style={{ maskType: "alpha" }}
											width="40"
											x="0"
											y="0"
										>
											<rect
												fill="#D9D9D9"
												height="40"
												width="40"
											/>
										</mask>
										<g mask="url(#mask0_integrity)">
											<path
												d={svgMobileAbout.p2f944400}
												fill="#25BC45"
											/>
										</g>
									</svg>
								</div>
								<div className="flex-1 leading-5 tracking-[0.5px]">
									<h3 className="text-white text-sm font-black mb-0">
										Integrity
									</h3>
									<p className="text-[rgba(255,255,255,0.7)] text-xs">
										We put investors&apos; interests first
										in every decision.
									</p>
								</div>
							</div>

							{/* Innovation */}
							<div className="flex gap-3 items-start">
								<div className="w-10 h-10 shrink-0">
									<svg
										className="block size-full"
										fill="none"
										viewBox="0 0 40 40"
									>
										<mask
											height="40"
											id="mask0_innovation"
											maskUnits="userSpaceOnUse"
											style={{ maskType: "alpha" }}
											width="40"
											x="0"
											y="0"
										>
											<rect
												fill="#D9D9D9"
												height="40"
												width="40"
											/>
										</mask>
										<g mask="url(#mask0_innovation)">
											<path
												d={svgMobileAbout.p1983b5f2}
												fill="#25BC45"
											/>
										</g>
									</svg>
								</div>
								<div className="flex-1 leading-5 tracking-[0.5px]">
									<h3 className="text-white text-sm font-black mb-0">
										Innovation
									</h3>
									<p className="text-[rgba(255,255,255,0.7)] text-xs">
										We continuously evolve our tools so you
										can stay ahead in a competitive market.
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Why Investors Trust Us */}
					<div className="mx-4 mb-8">
						<h2 className="text-white text-xl font-bold leading-7 tracking-[0.5px] mb-5">
							Why Investors Trust Us
						</h2>

						<div className="flex flex-col gap-5">
							{/* Real-time data */}
							<div className="flex gap-3 items-start">
								<div className="w-10 h-10 shrink-0">
									<svg
										className="block size-full"
										fill="none"
										viewBox="0 0 40 40"
									>
										<mask
											height="40"
											id="mask0_trust1"
											maskUnits="userSpaceOnUse"
											style={{ maskType: "alpha" }}
											width="40"
											x="0"
											y="0"
										>
											<rect
												fill="#D9D9D9"
												height="40"
												width="40"
											/>
										</mask>
										<g mask="url(#mask0_trust1)">
											<path
												d={svgMobileAbout.p3e271900}
												fill="#25BC45"
											/>
										</g>
									</svg>
								</div>
								<div className="flex-1">
									<p className="text-white text-xs font-medium leading-[18px] tracking-[0.5px]">
										Real-time data verified against court
										and parcel records.
									</p>
								</div>
							</div>

							{/* Comprehensive coverage */}
							<div className="flex gap-3 items-start">
								<div className="w-10 h-10 shrink-0">
									<svg
										className="block size-full"
										fill="none"
										viewBox="0 0 40 40"
									>
										<mask
											height="40"
											id="mask0_trust2"
											maskUnits="userSpaceOnUse"
											style={{ maskType: "alpha" }}
											width="40"
											x="0"
											y="0"
										>
											<rect
												fill="#D9D9D9"
												height="40"
												width="40"
											/>
										</mask>
										<g mask="url(#mask0_trust2)">
											<path
												d={svgMobileAbout.p3e271900}
												fill="#25BC45"
											/>
										</g>
									</svg>
								</div>
								<div className="flex-1">
									<p className="text-white text-xs font-medium leading-[18px] tracking-[0.5px]">
										Comprehensive coverage across multiple
										states.
									</p>
								</div>
							</div>

							{/* Built by investors */}
							<div className="flex gap-3 items-start">
								<div className="w-10 h-10 shrink-0">
									<svg
										className="block size-full"
										fill="none"
										viewBox="0 0 40 40"
									>
										<mask
											height="40"
											id="mask0_trust3"
											maskUnits="userSpaceOnUse"
											style={{ maskType: "alpha" }}
											width="40"
											x="0"
											y="0"
										>
											<rect
												fill="#D9D9D9"
												height="40"
												width="40"
											/>
										</mask>
										<g mask="url(#mask0_trust3)">
											<path
												d={svgMobileAbout.p3e271900}
												fill="#25BC45"
											/>
										</g>
									</svg>
								</div>
								<div className="flex-1">
									<p className="text-white text-xs font-medium leading-[18px] tracking-[0.5px]">
										Built by investors, for investors—we
										understand your needs.
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* CTA Section - See Inside the Platform */}
					<div className="backdrop-blur-md px-6 py-8 mb-0">
						<div className="flex flex-col gap-7">
							<h2 className="text-white text-xl font-extrabold leading-7 text-center tracking-[0.5px]">
								See Inside the Platform – Access Professional
								Property Intelligence Today
							</h2>

							<div className="flex flex-col gap-5">
								{/* Search Input */}
								<div className="bg-white rounded-[68px] px-4 py-2 flex items-center gap-1 border border-[#dde3e9] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] h-[56px]">
									<input
										type="text"
										placeholder="State, County, City or Zip code "
										className="flex-1 text-sm font-medium text-[#697b8e] bg-transparent outline-none leading-5 tracking-[-0.1504px]"
									/>
									<div className="bg-[#a7ff5e] w-10 h-10 rounded-[44px] flex items-center justify-center shrink-0 border border-[#12301b]">
										<svg
											className="w-[14px] h-[14px]"
											fill="none"
											viewBox="0 0 17 17"
										>
											<path
												d={svgMobileAbout.p22e3b400}
												stroke="#12301B"
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2.5"
											/>
										</svg>
									</div>
								</div>

								{/* CTA Text */}
								<button
									onClick={handleUpgrade}
									disabled={isUpgrading}
									className="text-xs leading-5 text-center tracking-[0.12px] bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed w-full"
									type="button"
								>
									<span className="text-[#cbf7a6] font-semibold">
										Try Free for 14 days
									</span>
									<span className="text-[#dde3e9]">
										{" "}
										• No Credit Card Required • Cancel
										Anytime • Priority Support
									</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Footer */}
			<div className="bg-[#1a2c20] px-8 pt-6 pb-8">
				<div className="flex flex-col gap-8">
					{/* Footer Links */}
					<div className="flex items-center justify-center gap-[42px] text-white text-xs font-medium leading-5">
						<Link href="/about" className="whitespace-nowrap">
							About
						</Link>
						<Link
							href="/privacy-policy"
							className="whitespace-nowrap"
						>
							Privacy Policy
						</Link>
						<Link
							href="/terms-of-service"
							className="whitespace-nowrap"
						>
							Terms & Conditions
						</Link>
						<Link href="/contact-us" className="whitespace-nowrap">
							Contact
						</Link>
					</div>

					{/* Social and Copyright Section */}
					<div className="flex flex-col gap-8 pt-8 border-t border-[#417714]">
						{/* Copyright */}
						<p className="text-[#dde3e9] text-sm text-center leading-5">
							© 2025 Lein-Vest. All rights reserved.
						</p>

						{/* Terms */}
						<p className="text-[#dde3e9] text-sm text-center leading-5">
							<span className="underline decoration-solid">
								Terms and Condition
							</span>{" "}
							and{" "}
							<span className="underline decoration-solid">
								Privacy policy
							</span>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
